import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Zap, Send, Mail, Database, ArrowRight, Calendar } from 'lucide-react';
import { theme } from '../../../styles/theme';
import { ACTIONS } from '../../../hooks/useWizardForm';
import StepHint from '../StepHint';
import TriggerTypeSelector from '../TriggerTypeSelector';
import ResultPathHelper from '../ResultPathHelper';

const LazyPayloadSchemaEditor = React.lazy(() => import('../PayloadSchemaEditor'));

function LoadingFallback() {
  const { t } = useTranslation('campaigns');
  return (
    <FallbackContainer>
      <FallbackText>{t('stepTrigger.loadingSchemaEditor')}</FallbackText>
    </FallbackContainer>
  );
}

export default function StepTriggerData({ state, dispatch, errors, connectors, connectorTypes, availableFields, agents }) {
  const { t } = useTranslation('campaigns');
  const triggerType = state.triggerConfig?.type || 'manual';

  const handleTriggerTypeChange = (type) => {
    dispatch({ type: ACTIONS.SET_TRIGGER_TYPE, payload: { triggerType: type } });
  };

  const handleFieldChange = (path, value) => {
    dispatch({ type: ACTIONS.SET_FIELD, payload: { path, value } });
  };

  // Get the selected agent's enabledActions to filter tools
  const selectedAgent = (agents || []).find(
    (a) => a.instanceId === state.instanceId
  );
  const enabledActions = selectedAgent?.enabledActions || null; // null = all allowed

  // Filter connectors linked to the selected agent and enrich with tools from connector types
  // Only show tools that are in the agent's enabledActions list
  const dataSourceConnectors = (connectors || [])
    .filter((c) => c.enabled !== false && c.agentInstanceId === state.instanceId)
    .map((c) => {
      const ct = (connectorTypes || []).find((t) => t.type === c.type);
      let tools = ct?.capabilities?.tools || [];
      // Filter by agent's enabledActions (null/undefined = all allowed, empty array = none)
      if (Array.isArray(enabledActions)) {
        tools = tools.filter((t) => enabledActions.includes(t.name));
      }
      return { ...c, id: c.connectorId, tools };
    })
    .filter((c) => c.tools.length > 0);

  const selectedConnector = dataSourceConnectors.find(
    (c) => c.id === state.dataSource.connectorId || c.connectorId === state.dataSource.connectorId
  );

  const availableTools = selectedConnector?.tools || [];

  // Get selected tool definition for hint display
  const selectedTool = availableTools.find(
    (t) => t.name === state.dataSource.toolName
  );

  const generateToken = () => {
    const token = crypto.randomUUID
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
    handleFieldChange('triggerConfig.verifyToken', token);
  };

  return (
    <Container>
      <StepHint
        icon={Zap}
        title={t('stepTrigger.hintTitle')}
        description={t('stepTrigger.hintDescription')}
      />

      <Section>
        <TriggerTypeSelector
          value={triggerType}
          onChange={handleTriggerTypeChange}
        />
      </Section>

      {(triggerType === 'manual' || triggerType === 'scheduled') && (
        <ManualScheduledSection
          state={state}
          errors={errors}
          dataSourceConnectors={dataSourceConnectors}
          availableTools={availableTools}
          selectedTool={selectedTool}
          onFieldChange={handleFieldChange}
        />
      )}

      {triggerType === 'scheduled' && (
        <ScheduleConfigSection
          state={state}
          errors={errors}
          onFieldChange={handleFieldChange}
        />
      )}

      {triggerType === 'endpoint' && (
        <EndpointSection
          state={state}
          errors={errors}
          onFieldChange={handleFieldChange}
          onGenerateToken={generateToken}
        />
      )}

      {triggerType === 'email' && (
        <EmailSection
          state={state}
          errors={errors}
          onFieldChange={handleFieldChange}
        />
      )}
    </Container>
  );
}

// ─── Manual / Scheduled Section ──────────────────────────────────────────────

function ManualScheduledSection({ state, errors, dataSourceConnectors, availableTools, selectedTool, onFieldChange }) {
  const { t } = useTranslation('campaigns');
  // JSON validation for params field
  const paramsValidation = (() => {
    const raw = (state.dataSource.params || '').trim();
    if (!raw || raw === '{}') return { valid: true, error: null };
    try {
      JSON.parse(raw);
      return { valid: true, error: null };
    } catch (e) {
      return { valid: false, error: e.message };
    }
  })();

  // Local state for filters textarea (allows typing invalid JSON)
  const [filtersText, setFiltersText] = React.useState(() =>
    Array.isArray(state.dataSource.filters) && state.dataSource.filters.length > 0
      ? JSON.stringify(state.dataSource.filters, null, 2)
      : '[]'
  );
  const filtersValidation = (() => {
    const raw = filtersText.trim();
    if (!raw || raw === '[]') return { valid: true, error: null };
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return { valid: false, error: t('stepTrigger.mustBeArray') };
      return { valid: true, error: null };
    } catch (e) {
      return { valid: false, error: e.message };
    }
  })();
  const handleFiltersChange = (e) => {
    const val = e.target.value;
    setFiltersText(val);
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) {
        onFieldChange('dataSource.filters', parsed);
      }
    } catch {
      // Allow typing — state updates only when valid
    }
  };

  return (
    <Section>
      <SectionTitle>
        <Database size={16} />
        {t('stepTrigger.dataSource')}
      </SectionTitle>

      <FormField>
        <FieldLabel>{t('stepTrigger.dataConnector')}</FieldLabel>
        <SelectInput
          value={state.dataSource.connectorId}
          onChange={(e) => onFieldChange('dataSource.connectorId', e.target.value)}
          $hasError={!!errors['dataSource.connectorId']}
        >
          <option value="">{t('stepTrigger.selectConnector')}</option>
          {dataSourceConnectors.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </SelectInput>
        {errors['dataSource.connectorId'] && (
          <ErrorMessage>{errors['dataSource.connectorId']}</ErrorMessage>
        )}
      </FormField>

      <FormField>
        <FieldLabel>{t('stepTrigger.functionTool')}</FieldLabel>
        <SelectInput
          value={state.dataSource.toolName}
          onChange={(e) => onFieldChange('dataSource.toolName', e.target.value)}
          $hasError={!!errors['dataSource.toolName']}
          disabled={!state.dataSource.connectorId}
        >
          <option value="">{t('stepTrigger.selectFunction')}</option>
          {availableTools.map((tool) => (
            <option key={tool.name} value={tool.name}>{tool.name}</option>
          ))}
        </SelectInput>
        {errors['dataSource.toolName'] && (
          <ErrorMessage>{errors['dataSource.toolName']}</ErrorMessage>
        )}
      </FormField>

      {selectedTool && (
        <ToolHintBox>
          <ToolHintTitle>{selectedTool.name}</ToolHintTitle>
          <ToolHintDescription>{selectedTool.description}</ToolHintDescription>
          {selectedTool.input_schema?.properties && (
            <ToolHintParams>
              <ToolHintParamsTitle>{t('stepTrigger.parametersLabel')}</ToolHintParamsTitle>
              {Object.entries(selectedTool.input_schema.properties).map(([key, def]) => (
                <ToolHintParam key={key}>
                  <ToolHintParamName $required={(selectedTool.input_schema.required || []).includes(key)}>
                    {key}
                  </ToolHintParamName>
                  <ToolHintParamDesc>{def.description || `(${def.type})`}</ToolHintParamDesc>
                </ToolHintParam>
              ))}
            </ToolHintParams>
          )}
        </ToolHintBox>
      )}

      <FormField>
        <FieldLabel>
          {t('stepTrigger.paramsLabel')}
          <Tooltip>
            <TooltipIcon>?</TooltipIcon>
            <span>{t('stepTrigger.paramsTooltip')}</span>
          </Tooltip>
        </FieldLabel>
        <TextareaInput
          value={state.dataSource.params}
          onChange={(e) => onFieldChange('dataSource.params', e.target.value)}
          placeholder='{"limit": 100}'
          rows={4}
          $hasError={!paramsValidation.valid}
        />
        {!paramsValidation.valid && (
          <JsonErrorMessage>{t('stepTrigger.invalidJson', { error: paramsValidation.error })}</JsonErrorMessage>
        )}
        <MacrosHint>
          <MacrosTitle>{t('stepTrigger.macrosTitle')}</MacrosTitle>
          <MacrosList>
            <MacroItem><code>{'{{today}}'}</code> {t('stepTrigger.macroToday')}</MacroItem>
            <MacroItem><code>{'{{today+1}}'}</code> {t('stepTrigger.macroTomorrow')}</MacroItem>
            <MacroItem><code>{'{{today+2}}'}</code> {t('stepTrigger.macroDayAfter')}</MacroItem>
            <MacroItem><code>{'{{today+N}}'}</code> {t('stepTrigger.macroInNDays')}</MacroItem>
            <MacroItem><code>{'{{today-1}}'}</code> {t('stepTrigger.macroYesterday')}</MacroItem>
            <MacroItem><code>{'{{tomorrow}}'}</code> <code>{'{{yesterday}}'}</code> <code>{'{{weekStart}}'}</code> <code>{'{{monthStart}}'}</code></MacroItem>
            <MacroItem><code>{'{{today_br+N}}'}</code> {t('stepTrigger.macroBrFormat')}</MacroItem>
          </MacrosList>
        </MacrosHint>
      </FormField>

      <FormField>
        <FieldLabel>
          {t('stepTrigger.filtersLabel')}
          <Tooltip>
            <TooltipIcon>?</TooltipIcon>
            <span>{t('stepTrigger.filtersTooltip')}</span>
          </Tooltip>
        </FieldLabel>
        <TextareaInput
          value={filtersText}
          onChange={handleFiltersChange}
          placeholder='[{"field": "status", "operator": "eq", "value": "active"}]'
          rows={3}
          $hasError={!filtersValidation.valid}
        />
        {!filtersValidation.valid && (
          <JsonErrorMessage>{t('stepTrigger.invalidJson', { error: filtersValidation.error })}</JsonErrorMessage>
        )}
      </FormField>

      <FormField>
        <FieldLabel>
          {t('stepTrigger.resultPathLabel')}
          <Tooltip>
            <TooltipIcon>?</TooltipIcon>
            <span>{t('stepTrigger.resultPathTooltip')}</span>
          </Tooltip>
        </FieldLabel>
        <TextInput
          value={state.dataSource.resultPath || state.resultPath || ''}
          onChange={(e) => {
            onFieldChange('dataSource.resultPath', e.target.value);
            onFieldChange('resultPath', e.target.value);
          }}
          placeholder={t('stepTrigger.resultPathPlaceholder')}
        />
      </FormField>

      <FieldRow>
        <FormField style={{ flex: 1 }}>
          <FieldLabel>
            {t('stepTrigger.contactFieldLabel')}
            <Tooltip>
              <TooltipIcon>?</TooltipIcon>
              <span>{t('stepTrigger.contactFieldTooltip')}</span>
            </Tooltip>
          </FieldLabel>
          <TextInput
            value={state.dataSource.contactField || ''}
            onChange={(e) => onFieldChange('dataSource.contactField', e.target.value)}
            placeholder="telefone"
          />
        </FormField>
        <FormField style={{ flex: 1 }}>
          <FieldLabel>
            {t('stepTrigger.nameFieldLabel')}
            <Tooltip>
              <TooltipIcon>?</TooltipIcon>
              <span>{t('stepTrigger.nameFieldTooltip')}</span>
            </Tooltip>
          </FieldLabel>
          <TextInput
            value={state.dataSource.nameField || ''}
            onChange={(e) => onFieldChange('dataSource.nameField', e.target.value)}
            placeholder="nome"
          />
        </FormField>
      </FieldRow>
    </Section>
  );
}

// ─── Schedule Config Section ─────────────────────────────────────────────────

function ScheduleConfigSection({ state, errors, onFieldChange }) {
  const { t } = useTranslation('campaigns');

  const CRON_PRESETS = [
    { label: t('stepTrigger.cronDaily9'), expr: 'cron(0 9 * * * *)' },
    { label: t('stepTrigger.cronWeekdays8'), expr: 'cron(0 8 * * MON-FRI *)' },
    { label: t('stepTrigger.cronWeeklyMon9'), expr: 'cron(0 9 * * MON *)' },
    { label: t('stepTrigger.cronMonthly1'), expr: 'cron(0 9 1 * * *)' },
  ];

  const schedule = state.schedule || {};
  const mode = schedule.mode || 'once';

  const handleModeChange = (newMode) => {
    onFieldChange('schedule.mode', newMode);
    // Clear the other field when switching
    if (newMode === 'once') {
      onFieldChange('schedule.cronExpression', '');
    } else {
      onFieldChange('schedule.scheduledAt', '');
    }
  };

  return (
    <Section>
      <SectionTitle>
        <Calendar size={16} />
        {t('stepTrigger.schedulingTitle')}
      </SectionTitle>

      <ScheduleModeToggle>
        <ScheduleModeBtn
          $active={mode === 'once'}
          onClick={() => handleModeChange('once')}
          type="button"
        >
          {t('stepTrigger.modeOnce')}
        </ScheduleModeBtn>
        <ScheduleModeBtn
          $active={mode === 'recurrent'}
          onClick={() => handleModeChange('recurrent')}
          type="button"
        >
          {t('stepTrigger.modeRecurrent')}
        </ScheduleModeBtn>
      </ScheduleModeToggle>

      {mode === 'once' ? (
        <FormField>
          <FieldLabel>{t('stepTrigger.dateTimeLabel')}</FieldLabel>
          <TextInput
            type="datetime-local"
            value={schedule.scheduledAt || ''}
            onChange={(e) => onFieldChange('schedule.scheduledAt', e.target.value)}
            min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
            $hasError={!!errors['schedule.scheduledAt']}
          />
          {errors['schedule.scheduledAt'] && (
            <ErrorMessage>{errors['schedule.scheduledAt']}</ErrorMessage>
          )}
          <FieldHint>{t('stepTrigger.onceHint')}</FieldHint>
        </FormField>
      ) : (
        <FormField>
          <FieldLabel>{t('stepTrigger.cronExpressionLabel')}</FieldLabel>
          <TextInput
            value={schedule.cronExpression || ''}
            onChange={(e) => onFieldChange('schedule.cronExpression', e.target.value)}
            placeholder="rate(1 day) ou cron(0 9 * * MON-FRI *)"
            $hasError={!!errors['schedule.cronExpression']}
          />
          {errors['schedule.cronExpression'] && (
            <ErrorMessage>{errors['schedule.cronExpression']}</ErrorMessage>
          )}
          <CronPresetsRow>
            {CRON_PRESETS.map((preset) => (
              <CronPresetChip
                key={preset.expr}
                type="button"
                onClick={() => onFieldChange('schedule.cronExpression', preset.expr)}
              >
                {preset.label}
              </CronPresetChip>
            ))}
          </CronPresetsRow>
          <FieldHint>{t('stepTrigger.recurrentHint')}</FieldHint>
        </FormField>
      )}
    </Section>
  );
}

// ─── Endpoint Section ────────────────────────────────────────────────────────

function EndpointSection({ state, errors, onFieldChange, onGenerateToken }) {
  const { t } = useTranslation('campaigns');
  return (
    <Section>
      <InfoBox $variant="warning">
        <InfoBoxIcon>
          <Send size={16} />
        </InfoBoxIcon>
        <InfoBoxContent>
          <InfoBoxTitle>{t('stepTrigger.endpointInfoTitle')}</InfoBoxTitle>
          <InfoBoxDescription>
            {t('stepTrigger.endpointFlowExternal')} <ArrowRight size={12} style={{ verticalAlign: 'middle' }} /> POST <ArrowRight size={12} style={{ verticalAlign: 'middle' }} /> {t('stepTrigger.endpointFlowCampaign')} <ArrowRight size={12} style={{ verticalAlign: 'middle' }} /> {t('stepTrigger.endpointFlowContacts')}
          </InfoBoxDescription>
          <InfoBoxDescription>
            {t('stepTrigger.endpointInfoDescription')}
          </InfoBoxDescription>
        </InfoBoxContent>
      </InfoBox>

      <InfoBox $variant="info">
        <InfoBoxIcon>
          <Database size={16} />
        </InfoBoxIcon>
        <InfoBoxContent>
          <InfoBoxDescription>
            {t('stepTrigger.endpointNoConnector')}
          </InfoBoxDescription>
        </InfoBoxContent>
      </InfoBox>

      <FormField>
        <FieldLabel>{t('stepTrigger.verifyToken')}</FieldLabel>
        <TokenRow>
          <TextInput
            value={state.triggerConfig?.verifyToken || ''}
            onChange={(e) => onFieldChange('triggerConfig.verifyToken', e.target.value)}
            placeholder={t('stepTrigger.verifyTokenPlaceholder')}
            $hasError={!!errors.verifyToken}
            readOnly
          />
          <GenerateButton onClick={onGenerateToken} type="button">
            {t('stepTrigger.generateToken')}
          </GenerateButton>
        </TokenRow>
        {errors.verifyToken && (
          <ErrorMessage>{errors.verifyToken}</ErrorMessage>
        )}
        <TokenHint>
          {t('stepTrigger.tokenHintPrefix')} <code>token</code> {t('stepTrigger.tokenHintSuffix')}
        </TokenHint>
        <TokenUsageCode>
{`POST <url_do_trigger>
Content-Type: application/json
token: ${state.triggerConfig?.verifyToken || '<seu_token>'}`}
        </TokenUsageCode>
      </FormField>

      <FormField>
        <FieldLabel>{t('stepTrigger.rateLimitSecondsLabel')}</FieldLabel>
        <RateLimitRow>
          <TextInput
            type="number"
            min="0"
            value={state.triggerConfig?.rateLimitSeconds ?? 60}
            onChange={(e) => onFieldChange('triggerConfig.rateLimitSeconds', parseInt(e.target.value, 10) || 0)}
            placeholder="60"
            style={{ width: '100px' }}
          />
          <RateLimitHint>
            {(state.triggerConfig?.rateLimitSeconds ?? 60) === 0
              ? t('stepTrigger.rateLimitDisabled')
              : t('stepTrigger.rateLimitMax', { seconds: state.triggerConfig?.rateLimitSeconds || 60 })
            }
          </RateLimitHint>
        </RateLimitRow>
      </FormField>

      <FormField>
        <ResultPathHelper
          value={state.resultPath || ''}
          onChange={(val) => onFieldChange('resultPath', val)}
          payloadSchema={state.payloadSchema}
        />
      </FormField>

      <FormField>
        <FieldLabel>{t('stepTrigger.payloadSchema')}</FieldLabel>
        <React.Suspense fallback={<LoadingFallback />}>
          <LazyPayloadSchemaEditor
            payloadSchema={state.payloadSchema}
            onChange={(schema) => onFieldChange('payloadSchema', schema)}
          />
        </React.Suspense>
      </FormField>

      <RateLimitSection>
        <SectionTitle>{t('stepTrigger.rateLimitTitle')}</SectionTitle>
        <RateLimitGrid>
          <FormField>
            <FieldLabel>{t('stepTrigger.batchSize')}</FieldLabel>
            <NumberInput
              type="number"
              value={state.rateLimit?.batchSize || 30}
              onChange={(e) => onFieldChange('rateLimit.batchSize', parseInt(e.target.value, 10) || 30)}
              min={1}
            />
          </FormField>
          <FormField>
            <FieldLabel>{t('stepTrigger.delayBetweenBatches')}</FieldLabel>
            <NumberInput
              type="number"
              value={state.rateLimit?.delayMs || 0}
              onChange={(e) => onFieldChange('rateLimit.delayMs', parseInt(e.target.value, 10) || 0)}
              min={0}
            />
          </FormField>
        </RateLimitGrid>
      </RateLimitSection>
    </Section>
  );
}

// ─── Email Section ───────────────────────────────────────────────────────────

function EmailSection({ state, errors, onFieldChange }) {
  const { t } = useTranslation('campaigns');
  return (
    <Section>
      <InfoBox $variant="info">
        <InfoBoxIcon>
          <Mail size={16} />
        </InfoBoxIcon>
        <InfoBoxContent>
          <InfoBoxTitle>{t('stepTrigger.emailInfoTitle')}</InfoBoxTitle>
          <InfoBoxDescription>
            {t('stepTrigger.emailInfoDescription')}
          </InfoBoxDescription>
        </InfoBoxContent>
      </InfoBox>

      <FormField>
        <FieldLabel>{t('stepTrigger.authorizedEmails')}</FieldLabel>
        <TextInput
          value={state.triggerConfig?.authorizedEmails || ''}
          onChange={(e) => onFieldChange('triggerConfig.authorizedEmails', e.target.value)}
          placeholder="email1@example.com, email2@example.com"
          $hasError={!!errors.authorizedEmails}
        />
        <FieldHint>{t('stepTrigger.separateEmailsHint')}</FieldHint>
        {errors.authorizedEmails && (
          <ErrorMessage>{errors.authorizedEmails}</ErrorMessage>
        )}
      </FormField>

      <FormField>
        <ResultPathHelper
          value={state.resultPath || ''}
          onChange={(val) => onFieldChange('resultPath', val)}
          payloadSchema={state.payloadSchema}
        />
      </FormField>

      <FormField>
        <FieldLabel>{t('stepTrigger.payloadSchema')}</FieldLabel>
        <React.Suspense fallback={<LoadingFallback />}>
          <LazyPayloadSchemaEditor
            payloadSchema={state.payloadSchema}
            onChange={(schema) => onFieldChange('payloadSchema', schema)}
          />
        </React.Suspense>
      </FormField>
    </Section>
  );
}

// ─── Styled Components ───────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const FieldRow = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};

  @media (max-width: 639px) {
    flex-direction: column;
  }
`;

const FieldLabel = styled.label`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
`;

const FieldHint = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;

const SelectInput = styled.select`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.typography.sm};
  font-family: ${theme.typography.fontFamily};
  border: 1px solid ${(p) => (p.$hasError ? theme.colors.danger : theme.colors.border)};
  border-radius: ${theme.radii.md};
  background: ${theme.colors.white};
  color: ${theme.colors.textPrimary};
  transition: ${theme.transitions.fast};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }

  &:disabled {
    background: ${theme.colors.bgSecondary};
    color: ${theme.colors.textMuted};
    cursor: not-allowed;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.typography.sm};
  font-family: ${theme.typography.fontFamily};
  border: 1px solid ${(p) => (p.$hasError ? theme.colors.danger : theme.colors.border)};
  border-radius: ${theme.radii.md};
  background: ${theme.colors.white};
  color: ${theme.colors.textPrimary};
  transition: ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const NumberInput = styled(TextInput)`
  max-width: 160px;
`;

const TextareaInput = styled.textarea`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.typography.sm};
  font-family: ${theme.typography.fontMono};
  border: 1px solid ${(p) => p.$hasError ? theme.colors.danger : theme.colors.border};
  border-radius: ${theme.radii.md};
  background: ${(p) => p.$hasError ? '#fff5f5' : theme.colors.white};
  color: ${theme.colors.textPrimary};
  resize: vertical;
  transition: ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${(p) => p.$hasError ? theme.colors.danger : theme.colors.primary};
    box-shadow: 0 0 0 3px ${(p) => p.$hasError ? 'rgba(220,38,38,0.1)' : theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const TokenRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  align-items: center;
`;

const TokenHint = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing[2]};

  code {
    font-family: ${theme.typography.fontMono || 'monospace'};
    background: ${theme.colors.gray100};
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 0.7rem;
  }
`;

const TokenUsageCode = styled.pre`
  font-family: ${theme.typography.fontMono || 'monospace'};
  font-size: 11px;
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  margin: ${theme.spacing[2]} 0 0 0;
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.6;
`;

const RateLimitRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const RateLimitHint = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;

const GenerateButton = styled.button`
  flex-shrink: 0;
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.white};
  background: ${theme.colors.primary};
  border: none;
  border-radius: ${theme.radii.md};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  white-space: nowrap;

  &:hover {
    background: ${theme.colors.primaryHover};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ErrorMessage = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.danger};
`;

const JsonErrorMessage = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.danger};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: ${theme.radii.sm};
  font-family: ${theme.typography.fontMono || 'monospace'};
  margin-top: 4px;
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.radii.md};
  background: ${(p) => (p.$variant === 'warning' ? theme.colors.warningLight : theme.colors.infoLight)};
  border: 1px solid ${(p) => (p.$variant === 'warning' ? theme.colors.warningBorder : theme.colors.infoBorder)};
`;

const InfoBoxIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${theme.colors.textSecondary};
  margin-top: 2px;
`;

const InfoBoxContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoBoxTitle = styled.span`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
`;

const InfoBoxDescription = styled.span`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.snug};
`;

const RateLimitSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  padding-top: ${theme.spacing[3]};
  border-top: 1px solid ${theme.colors.border};
`;

const RateLimitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};

  @media (max-width: 639px) {
    grid-template-columns: 1fr;
  }
`;

const FallbackContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[8]};
  background: ${theme.colors.bgSecondary};
  border: 1px dashed ${theme.colors.border};
  border-radius: ${theme.radii.md};
`;

const FallbackText = styled.span`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
`;

const ToolHintBox = styled.div`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.infoLight || '#f0f7ff'};
  border: 1px solid ${theme.colors.infoBorder || '#bfdbfe'};
  border-radius: ${theme.radii.md};
`;

const ToolHintTitle = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  font-family: ${theme.typography.fontMono || 'monospace'};
  margin-bottom: ${theme.spacing[1]};
`;

const ToolHintDescription = styled.div`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.snug || '1.4'};
  margin-bottom: ${theme.spacing[2]};
`;

const ToolHintParams = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const ToolHintParamsTitle = styled.div`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
`;

const ToolHintParam = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing[2]};
  padding: 2px 0;
`;

const ToolHintParamName = styled.code`
  font-size: 11px;
  font-family: ${theme.typography.fontMono || 'monospace'};
  background: ${theme.colors.white};
  padding: 1px 4px;
  border-radius: 3px;
  border: 1px solid ${theme.colors.border};
  color: ${(p) => p.$required ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${(p) => p.$required ? theme.typography.semibold : 'normal'};
  flex-shrink: 0;
`;

const ToolHintParamDesc = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;

const MacrosHint = styled.div`
  margin-top: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.gray50 || '#f9fafb'};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
`;

const MacrosTitle = styled.div`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textSecondary};
  margin-bottom: 4px;
`;

const MacrosList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
`;

const MacroItem = styled.span`
  font-size: 11px;
  color: ${theme.colors.textMuted};

  code {
    font-family: ${theme.typography.fontMono || 'monospace'};
    background: ${theme.colors.white};
    padding: 1px 3px;
    border-radius: 2px;
    border: 1px solid ${theme.colors.border};
    font-size: 10px;
    color: ${theme.colors.primary};
  }
`;

const Tooltip = styled.span`
  position: relative;
  display: inline-flex;
  margin-left: ${theme.spacing[1]};

  &:hover > span:last-child {
    visibility: visible;
    opacity: 1;
  }

  & > span:last-child {
    visibility: hidden;
    opacity: 0;
    position: absolute;
    left: 50%;
    bottom: calc(100% + 6px);
    transform: translateX(-50%);
    width: 280px;
    padding: ${theme.spacing[2]} ${theme.spacing[3]};
    background: ${theme.colors.gray800 || '#1f2937'};
    color: ${theme.colors.white};
    font-size: ${theme.typography.xs};
    font-weight: normal;
    line-height: 1.4;
    border-radius: ${theme.radii.md};
    z-index: 100;
    pointer-events: none;
    transition: opacity 0.15s;
    white-space: normal;
  }
`;

const TooltipIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: ${theme.radii.full};
  background: ${theme.colors.gray200 || '#e5e7eb'};
  color: ${theme.colors.textMuted};
  font-size: 10px;
  font-weight: ${theme.typography.semibold};
  cursor: help;
`;

const ScheduleModeToggle = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[1]};
  background: ${theme.colors.bgSecondary};
  border-radius: ${theme.radii.md};
  border: 1px solid ${theme.colors.border};
`;

const ScheduleModeBtn = styled.button`
  flex: 1;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  font-family: ${theme.typography.fontFamily};
  border: none;
  border-radius: ${theme.radii.sm};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  background: ${(p) => (p.$active ? theme.colors.white : 'transparent')};
  color: ${(p) => (p.$active ? theme.colors.primary : theme.colors.textMuted)};
  box-shadow: ${(p) => (p.$active ? theme.shadows.sm : 'none')};

  &:hover {
    color: ${(p) => (p.$active ? theme.colors.primary : theme.colors.textPrimary)};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const CronPresetsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[2]};
`;

const CronPresetChip = styled.button`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  font-size: ${theme.typography.xs};
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.primary};
  background: ${theme.colors.primaryLight || '#eff6ff'};
  border: 1px solid ${theme.colors.primary}33;
  border-radius: ${theme.radii.full};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  white-space: nowrap;

  &:hover {
    background: ${theme.colors.primary}15;
    border-color: ${theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;
